import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daij8bboh653ivfvoe80";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function ConnectForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const rawMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const serverMsg = typeof rawMessage === "string" ? rawMessage : "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被识别为异常，请检查后重新填写。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <form
      id="hg-connect-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-card border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-xl font-bold text-foreground-950">投稿爆料 · 连线我们</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        有想聊的话题、想补充的线索，或者对内容有不同看法，都可以在这里告诉我们。我们会认真读完每一条连线。
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <label className="flex flex-1 flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground-800">你的昵称</span>
            <input
              type="text"
              name="nickname"
              required
              placeholder="想让我们怎么称呼你"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground-800">联系邮箱</span>
            <input
              type="email"
              name="email"
              required
              placeholder="方便我们回复你"
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">连线类型</span>
          <select
            name="connect_type"
            defaultValue="投稿爆料"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="投稿爆料">投稿爆料</option>
            <option value="话题推荐">话题推荐</option>
            <option value="内容纠错">内容纠错</option>
            <option value="合作交流">合作交流</option>
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">想说的话</span>
          <textarea
            name="message"
            required
            rows={5}
            maxLength={500}
            placeholder="写清楚话题背景和你关注的点，500 字以内"
            className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>

        <input
          type="text"
          name="website_alt"
          className="honeypot-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            提交即表示你同意我们整理并发布相关内容，署名方式可另行沟通。
          </p>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={status === "submitting" ? "ri-loader-4-line text-sm" : "ri-send-plane-fill text-sm"} />
            </span>
            {status === "submitting" ? "提交中…" : "提交连线"}
          </button>
        </div>

        {status === "success" && (
          <p className="flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-checkbox-circle-fill text-base" />
            </span>
            收到啦！你的连线已经成功提交，我们会尽快查看并回复。
          </p>
        )}
        {status === "error" && formError && (
          <p className="flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-fill text-base" />
            </span>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}